import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { Lock } from 'lucide-react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { SidebarProvider } from './contexts/SidebarContext';
import useSessionTimeout from './hooks/useSessionTimeout';
import useActivityTracker from './hooks/useActivityTracker';
import LegalConsentModal from './components/LegalConsentModal';
import CookieBanner from './components/CookieBanner';

// Componente de carga oficial Nexo CRM
const NexoLoader: React.FC<{ fullScreen?: boolean }> = ({ fullScreen = false }) => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: fullScreen ? '100vh' : '80vh',
    background: '#f8fafc',
    flexDirection: 'column',
    gap: '20px',
    fontFamily: "'Plus Jakarta Sans', -apple-system, sans-serif"
  }}>
    <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <img
        src="/logo_nexo.png"
        style={{
          height: '44px',
          width: 'auto',
          objectFit: 'contain',
          marginBottom: '16px',
          animation: 'nexoPulse 1.8s ease-in-out infinite'
        }}
        alt="Nexo CRM"
      />
      <div style={{
        width: '140px',
        height: '4px',
        background: '#e2e8f0',
        borderRadius: '3px',
        overflow: 'hidden',
        position: 'relative'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          height: '100%',
          width: '50%',
          background: 'linear-gradient(90deg, #063c3d 0%, #00df82 100%)',
          borderRadius: '3px',
          animation: 'nexoBar 1.2s ease-in-out infinite'
        }} />
      </div>
    </div>
    <div style={{
      color: '#063c3d',
      fontSize: '12px',
      fontWeight: '600',
      letterSpacing: '0.3px',
      opacity: 0.85
    }}>
      Cargando Nexo CRM...
    </div>
    <style>{`
      @keyframes nexoPulse {
        0%, 100% { transform: scale(1); opacity: 1; }
        50% { transform: scale(0.97); opacity: 0.85; }
      }
      @keyframes nexoBar {
        0% { left: -40%; width: 30%; }
        50% { left: 35%; width: 50%; }
        100% { left: 110%; width: 30%; }
      }
    `}</style>
  </div>
);

const PageLoader = () => <NexoLoader />;

// Lazy Loading de páginas
const SuperAdmin = lazy(() => import('./pages/SuperAdmin'));
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const PaymentSuccess = lazy(() => import('./pages/PaymentSuccess'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Contacts = lazy(() => import('./pages/Contacts'));
const Conversations = lazy(() => import('./pages/Conversations'));
const AIAgents = lazy(() => import('./pages/AIAgents'));
const Campaigns = lazy(() => import('./pages/Campaigns'));
const Subscription = lazy(() => import('./pages/Subscription'));
const Settings = lazy(() => import('./pages/Settings'));
const RealEstate = lazy(() => import('./pages/RealEstate'));
const Team = lazy(() => import('./pages/Team'));
const Pipeline = lazy(() => import('./pages/Pipeline'));
const MetaAds = lazy(() => import('./pages/MetaAds'));
const SocialMedia = lazy(() => import('./pages/SocialMedia'));
const Lists = lazy(() => import('./pages/Lists'));
const Marketing = lazy(() => import('./pages/Marketing'));
const CalendarModule = lazy(() => import('./pages/CalendarModule'));
const LotMap = lazy(() => import('./pages/LotMap'));
const Contracts = lazy(() => import('./pages/Contracts'));
const Portfolio = lazy(() => import('./pages/Portfolio'));
const FinanceModule = lazy(() => import('./pages/FinanceModule'));
const SignContract = lazy(() => import('./pages/SignContract'));
const ClientPortal = lazy(() => import('./pages/ClientPortal'));
const LegalPolicies = lazy(() => import('./pages/LegalPolicies'));
const WebIntelligence = lazy(() => import('./pages/WebIntelligence'));
const PayOrder = lazy(() => import('./pages/PayOrder'));
const PaymentGateway = lazy(() => import('./pages/PaymentGateway'));
const InternalChat = lazy(() => import('./components/InternalChat'));

const PrivateRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { token, loading } = useAuth();

  if (loading) {
    return <NexoLoader fullScreen />;
  }

  return token ? <>{children}</> : <Navigate to="/login" />;
};

const SpecificAuthorizedRoute: React.FC<{ children: React.ReactNode; allowedEmails: string[] }> = ({ children, allowedEmails }) => {
  const { token, user, loading } = useAuth();
  if (loading) return <div>Cargando...</div>;
  if (!token) return <Navigate to="/login" />;
  const uEmail = (user?.email || '').toLowerCase();
  const isOwner = user?.role === 'OWNER';
  const isAllowed = isOwner || allowedEmails.some(e => e.toLowerCase() === uEmail);

  if (!isAllowed) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: '#f8fafc', padding: '24px' }}>
        <div style={{
          textAlign: 'center' as const,
          maxWidth: '440px',
          padding: '36px 32px',
          background: '#ffffff',
          borderRadius: '16px',
          border: '1.5px solid rgba(6, 60, 61, 0.18)',
          boxShadow: '0 4px 18px rgba(6, 60, 61, 0.05)'
        }}>
          <div style={{
            width: '56px',
            height: '56px',
            borderRadius: '14px',
            background: '#f8fafc',
            border: '1.5px solid rgba(6, 60, 61, 0.22)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px',
            color: '#063c3d'
          }}>
            <Lock size={26} strokeWidth={1.8} />
          </div>
          <h2 style={{ color: '#063c3d', fontSize: '20px', fontWeight: '700', marginBottom: '8px' }}>Acceso Exclusivo</h2>
          <p style={{ color: '#64748b', fontSize: '14px', lineHeight: '1.5', margin: 0 }}>Este módulo solo puede ser editado y gestionado por la administración.</p>
        </div>
      </div>
    );
  }
  return <>{children}</>;
};

const EdwcarOwnerRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, token, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (!token) return <Navigate to="/login" />;
  const isEdwcar = user?.tenantId === 'ad182dca-8ca0-4d1a-8e91-3cfdce6eb546' ||
                   user?.tenantName?.toLowerCase().includes('edwcar') ||
                   user?.companyName?.toLowerCase().includes('edwcar');
  if (isEdwcar && user?.role !== 'OWNER') {
    return <Navigate to="/pipeline" replace />;
  }
  return <>{children}</>;
};

const RealEstateRoute: React.FC = () => {
  const { user, token, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (!token) return <Navigate to="/login" />;
  const isEdwcar = user?.tenantId === 'ad182dca-8ca0-4d1a-8e91-3cfdce6eb546' ||
                   user?.tenantName?.toLowerCase().includes('edwcar') ||
                   user?.companyName?.toLowerCase().includes('edwcar');
  if (isEdwcar) {
    return <Navigate to="/pipeline" replace />;
  }
  return <PrivateRoute><RealEstate /></PrivateRoute>;
};

const RoleRoute: React.FC<{ children: React.ReactNode; roles: string[]; blockEmails?: string[] }> = ({ children, roles, blockEmails }) => {
  const { token, user, loading } = useAuth();
  if (loading) return <div>Cargando...</div>;
  if (!token) return <Navigate to="/login" />;
  const isBlockedByEmail = !!(user?.email && blockEmails?.includes(user.email.toLowerCase()));
  if (user && (!roles.includes(user.role) || isBlockedByEmail)) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: '#f8fafc', padding: '24px' }}>
        <div style={{
          textAlign: 'center' as const,
          maxWidth: '440px',
          padding: '36px 32px',
          background: '#ffffff',
          borderRadius: '16px',
          border: '1.5px solid rgba(6, 60, 61, 0.18)',
          boxShadow: '0 4px 18px rgba(6, 60, 61, 0.05)'
        }}>
          <div style={{
            width: '56px',
            height: '56px',
            borderRadius: '14px',
            background: '#f8fafc',
            border: '1.5px solid rgba(6, 60, 61, 0.22)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px',
            color: '#063c3d'
          }}>
            <Lock size={26} strokeWidth={1.8} />
          </div>
          <h2 style={{ color: '#063c3d', fontSize: '20px', fontWeight: '700', marginBottom: '8px' }}>Acceso restringido</h2>
          <p style={{ color: '#64748b', fontSize: '14px', lineHeight: '1.5', margin: 0 }}>No tienes permisos para ver esta sección.</p>
        </div>
      </div>
    );
  }
  return <>{children}</>;
};

function AppInner() {
  useSessionTimeout();
  useActivityTracker();
  return null;
}

function InternalChatWrapper() {
  // Chat de equipo desactivado temporalmente (el componente InternalChat.tsx se conserva intacto)
  return null;
}

const RootRoute: React.FC = () => {
  const { token, loading } = useAuth();
  if (loading) return <PageLoader />;
  return token ? <Navigate to="/dashboard" replace /> : <Navigate to="/login" replace />;
};

function App() {
  return (
    <ThemeProvider>
      <LanguageProvider>
        <SidebarProvider>
          <AuthProvider>
            <BrowserRouter>
              <AppInner />
              <Toaster position="top-right" />
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  <Route path="/" element={<RootRoute />} />
                  <Route path="/superadmin" element={<SuperAdmin />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/register" element={<Register />} />
                  <Route path="/payment-success" element={<PaymentSuccess />} />

                  <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
                  <Route path="/contacts" element={<PrivateRoute><Contacts /></PrivateRoute>} />
                  <Route path="/conversations" element={<PrivateRoute><Conversations /></PrivateRoute>} />
                  <Route path="/ai-agents" element={
                    <RoleRoute roles={['ADMIN', 'OWNER']}>
                      <EdwcarOwnerRoute>
                        <AIAgents />
                      </EdwcarOwnerRoute>
                    </RoleRoute>
                  } />
                  <Route path="/campaigns" element={<PrivateRoute><EdwcarOwnerRoute><Campaigns /></EdwcarOwnerRoute></PrivateRoute>} />
                  <Route path="/meta-ads" element={
                    <RoleRoute roles={['ADMIN', 'OWNER']}>
                      <EdwcarOwnerRoute>
                        <MetaAds />
                      </EdwcarOwnerRoute>
                    </RoleRoute>
                  } />
                  <Route path="/social" element={<PrivateRoute><EdwcarOwnerRoute><SocialMedia /></EdwcarOwnerRoute></PrivateRoute>} />
                  <Route path="/marketing" element={<PrivateRoute><EdwcarOwnerRoute><Marketing /></EdwcarOwnerRoute></PrivateRoute>} />
                  <Route path="/analytics" element={<PrivateRoute><EdwcarOwnerRoute><WebIntelligence /></EdwcarOwnerRoute></PrivateRoute>} />
                  <Route path="/heatmaps" element={<Navigate to="/analytics" replace />} />
                  <Route path="/trafico" element={<Navigate to="/analytics" replace />} />
                  <Route path="/lists" element={<PrivateRoute><EdwcarOwnerRoute><Lists /></EdwcarOwnerRoute></PrivateRoute>} />
                  <Route path="/automations/calendar" element={<PrivateRoute><EdwcarOwnerRoute><CalendarModule /></EdwcarOwnerRoute></PrivateRoute>} />
                  <Route path="/calendar" element={<PrivateRoute><EdwcarOwnerRoute><CalendarModule /></EdwcarOwnerRoute></PrivateRoute>} />

                  <Route path="/subscriptions" element={
                    <RoleRoute roles={['ADMIN', 'OWNER']}>
                      <EdwcarOwnerRoute>
                        <Subscription />
                      </EdwcarOwnerRoute>
                    </RoleRoute>
                  } />

                  <Route path="/settings" element={<PrivateRoute><Settings /></PrivateRoute>} />
                  <Route path="/team" element={<PrivateRoute><EdwcarOwnerRoute><Team /></EdwcarOwnerRoute></PrivateRoute>} />
                  <Route path="/pipeline" element={<PrivateRoute><Pipeline /></PrivateRoute>} />
                  <Route path="/real-estate" element={<RealEstateRoute />} />

                  {/* Pentágono Constructora & Urbanismo */}
                  <Route path="/mapa-lotes" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/contratos" element={<PrivateRoute><Contracts /></PrivateRoute>} />
                  <Route path="/cartera" element={<PrivateRoute><Portfolio /></PrivateRoute>} />
                  <Route path="/pasarela-pagos" element={<PrivateRoute><PaymentGateway /></PrivateRoute>} />
                  <Route path="/finanzas" element={<PrivateRoute><FinanceModule /></PrivateRoute>} />
                  <Route path="/balances" element={<Navigate to="/finanzas" replace />} />

                  {/* Rutas Públicas para Clientes y Representante Legal */}
                  <Route path="/firmar/:token" element={<SignContract />} />
                  <Route path="/firmar-representante/:token" element={<SignContract />} />
                  <Route path="/portal/:token" element={<ClientPortal />} />
                  <Route path="/pagar/:token" element={<PayOrder />} />

                  {/* Rutas de Políticas Legales y Habeas Data */}
                  <Route path="/politicas" element={<LegalPolicies />} />
                  <Route path="/terminos" element={<LegalPolicies />} />
                </Routes>
              </Suspense>
              <LegalConsentModal />
              <CookieBanner />
              <InternalChatWrapper />
            </BrowserRouter>
          </AuthProvider>
        </SidebarProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
